import { apiClient } from "@/services/apiServices";
import { FileDetails } from "@/types/types";
import imageCompression from "browser-image-compression";
import { sanitizeFileName } from "./sanitizeFileName";

const MAX_FILE_SIZE = 200 * 2048 * 2048;

const bucket_names_with_cdn = [
  {
    cdn_base_url: "https://assets.passionbits.io",
    s3_bucket_names: ["passionbits-core-files"],
  },
];

const getCdnBaseUrl = (bucket_name: string) => {
  for (const item of bucket_names_with_cdn) {
    if (item.s3_bucket_names.includes(bucket_name)) {
      return item.cdn_base_url;
    }
  }
  return null;
};

export const UploadFileToS3 = async ({
  file,
  filePath,
}: {
  file: FileDetails;
  bucketName?: string;
  filePath: string;
}): Promise<FileDetails> => {
  if (file?.size > MAX_FILE_SIZE) {
    throw new Error("File size exceeds the maximum limit of 100 MB.");
  }

  if (!filePath) {
    throw new Error("File path is required");
  }
  // sanitize the name
  const sanitizedName = sanitizeFileName(file?.name as string);

  const key = `ai-market-place-assets/${sanitizedName}`;
  try {
    const payload = {
      fileName: key,
      fileSize: file?.size,
      fileType: file.type,
      bucket_name: "passionbits-core-files",
    };
    const response = await apiClient.post("/upload/get-presigned-url", payload);

    const presignedUrl = response.data;

    let instanceOfFile = await createFileFromBlobUrl({ file });
    // Compress and convert to WebP
    const isImage = file.type.startsWith("image/");
    if (isImage) {
      instanceOfFile = await imageCompression(instanceOfFile, {
        maxSizeMB: 0.5, // 500KB
        maxWidthOrHeight: 1920,
        useWebWorker: true,
        fileType: "image/webp", // Convert to webp
      });
    }

    const uploadResponse = await fetch(presignedUrl.data, {
      method: "PUT",
      body: instanceOfFile,
    });

    if (!uploadResponse.ok) {
      throw new Error("Failed to upload file");
    }

    let file_live_url;

    // check if the bucket is connect with cdn or not
    const cdn_base_url = getCdnBaseUrl(payload.bucket_name);

    if (cdn_base_url) {
      // if it finds any cdn url then use that
      file_live_url = `${cdn_base_url}/${payload.fileName}`;
    } else {
      // else can use the default url
      file_live_url = presignedUrl.data.split("?")[0];
    }

    return {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      webkitRelativePath: file?.webkitRelativePath,
      local_url: file?.local_url,
      live_url: file_live_url,
    };
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

async function createFileFromBlobUrl({
  file,
}: {
  file: {
    local_url: string;
    name: string;
    lastModified: number;
    type: string;
  };
}): Promise<File> {
  const response = await fetch(file.local_url);
  const blob = await response.blob();

  return new File([blob], file.name, {
    lastModified: file.lastModified,
    type: file.type,
  });
}
